import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { showApiError, showSuccess, showWarning, showInfo } from "@/utils";

const POLLING_INTERVAL_MS = 15000;
const MAX_POLLING_FAILURES = 5;

const WorkflowStatusIcon = ({ status, onDismiss }) => {
  const { t } = useTranslation();
  const getStatusConfig = () => {
    switch (status) {
      case "running":
        return {
          icon: (
            <svg className="spinner" width="20" height="20" viewBox="0 0 20 20">
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="#34A853"
                strokeWidth="2"
                strokeDasharray="40"
                strokeDashoffset="10"
              />
            </svg>
          ),
          text: t("superAdmin.queryProcessing.running", "Running"),
          color: "#34A853",
        };
      case "completed":
        return {
          icon: (
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="#34A853" />
              <path
                d="M6 10 L9 13 L14 7"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          text: t("superAdmin.queryProcessing.completed", "Completed"),
          color: "#34A853",
        };
      case "failed":
        return {
          icon: (
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="#EA4335" />
              <path
                d="M10 6 L10 11"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="10" cy="14" r="1" fill="white" />
            </svg>
          ),
          text: t("superAdmin.queryProcessing.failed", "Failed"),
          color: "#EA4335",
        };
      case "cancelled":
        return {
          icon: (
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="#FBBC04" />
              <path
                d="M7 7 L13 13 M13 7 L7 13"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ),
          text: t("superAdmin.queryProcessing.cancelled", "Cancelled"),
          color: "#FBBC04",
        };
      case "unknown":
        return {
          icon: (
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="#9AA0A6" />
              <path
                d="M10 6 Q10 4, 11 4 Q12 4, 12 5 Q12 6, 11 7 L10 8 L10 10"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="10" cy="13" r="1" fill="white" />
            </svg>
          ),
          text: t("superAdmin.queryProcessing.unknown", "Unknown"),
          color: "#9AA0A6",
        };
      case "not_started":
        return null;
      default:
        return null;
    }
  };

  const config = getStatusConfig();

  if (!config) return null;

  return (
    <div
      className="d-inline-flex align-items-center gap-2 ms-2"
      style={{
        padding: "4px 8px",
        backgroundColor: `${config.color}15`,
        borderRadius: "4px",
        fontSize: "12px",
      }}
    >
      {config.icon}
      <span style={{ color: config.color, fontWeight: 500 }}>
        {config.text}
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss workflow status"
          style={{
            background: "none",
            border: "none",
            padding: "0",
            marginLeft: "4px",
            cursor: "pointer",
            color: config.color,
            opacity: 0.6,
            fontSize: "20px",
            lineHeight: "1",
          }}
          onMouseOver={(e) => (e.target.style.opacity = 1)}
          onMouseOut={(e) => (e.target.style.opacity = 0.6)}
        >
          ×
        </button>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

function SuperAdminQueryProcessing() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [allClients, setAllClients] = useState([]);
  const toggleButtonStyle = {
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#CED4DA",
  };

  // Workflow status tracking
  const [workflowStatuses, setWorkflowStatuses] = useState({
    uploadQueryGenProcessing: { status: "not_started", executionName: null },
    queryProcessing: { status: "not_started", executionName: null },
    postprocessing: { status: "not_started", executionName: null },
  });

  // Refs to store polling intervals
  const pollingIntervalsRef = useRef({});
  const failureCountsRef = useRef({});

  // Upload + Query Generation + Processing Workflow
  const [uploadQueryGenProcessingForm, setUploadQueryGenProcessingForm] =
    useState({
      clientId: "",
      file: null,
      active: false,
    });
  const [
    isUploadQueryGenProcessingRunning,
    setIsUploadQueryGenProcessingRunning,
  ] = useState(false);
  const [workflowAlert, setWorkflowAlert] = useState(null);

  // File Upload Only
  const [uploadForm, setUploadForm] = useState({
    clientId: "",
    fileId: "",
    file: null,
    active: false,
  });
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadAlert, setUploadAlert] = useState(null);

  // Curate CSV
  const [curateCSVForm, setCurateCSVForm] = useState({
    clientId: "",
    fileId: "",
    numOfURLs: null,
    active: false,
  });
  const [isCurateCSVLoading, setIsCurateCSVLoading] = useState(false);
  const [curateCSVAlert, setCurateCSVAlert] = useState(null);

  // Query Generation
  const [queryGenParams, setQueryGenParams] = useState({
    fileId: "",
    totalQueries: "",
  });
  const [isQueryGenerationLoading, setIsQueryGenerationLoading] =
    useState(false);

  // Query Processing
  const [queryProcessingFileId, setQueryProcessingFileId] = useState("");
  const [isQueryProcessingLoading, setIsQueryProcessingLoading] =
    useState(false);

  // Postprocessing Workflow
  const [isPostprocessingLoading, setIsPostprocessingLoading] = useState(false);

  // Fetch all clients on mount
  useEffect(() => {
    const fetchAllClients = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/client/all`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          const clientsData = await response.json();
          setAllClients(clientsData);
        } else {
          console.error(
            "Failed to fetch clients:",
            response.status,
            response.statusText,
          );
          showApiError(
            `Failed to fetch clients: ${response.status} ${response.statusText}`,
          );
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        showApiError(`Error fetching clients: ${error.message}`);
      }
    };

    fetchAllClients();
  }, [BACKEND_URL]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    const intervals = pollingIntervalsRef.current;
    return () => {
      Object.values(intervals).forEach((intervalId) => {
        if (intervalId) clearInterval(intervalId);
      });
    };
  }, []);

  // Auto-dismiss alerts after 60 seconds
  useEffect(() => {
    if (uploadAlert) {
      const timer = setTimeout(() => {
        setUploadAlert(null);
      }, 60000);

      return () => clearTimeout(timer);
    }
  }, [uploadAlert]);

  useEffect(() => {
    if (workflowAlert) {
      const timer = setTimeout(() => {
        setWorkflowAlert(null);
      }, 60000);

      return () => clearTimeout(timer);
    }
  }, [workflowAlert]);

  // Function to poll workflow status
  const pollWorkflowStatus = async (workflowKey, executionName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/geo-preprocessing/workflow-status?execution_name=${encodeURIComponent(executionName)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      // Check for critical HTTP errors that should stop polling immediately
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        console.error(
          `Critical error fetching workflow status: ${response.status}. Stopping polling.`,
        );
        stopPolling(workflowKey);
        setWorkflowStatuses((prev) => ({
          ...prev,
          [workflowKey]: {
            status: "unknown",
            executionName: executionName,
          },
        }));
        return;
      }

      if (!response.ok) {
        console.error(`Failed to fetch workflow status: ${response.status}`);
        // Increment failure count
        failureCountsRef.current[workflowKey] =
          (failureCountsRef.current[workflowKey] || 0) + 1;

        // Stop polling after MAX_POLLING_FAILURES = 5 consecutive failures
        if (failureCountsRef.current[workflowKey] >= MAX_POLLING_FAILURES) {
          console.error(
            `Stopping polling for ${workflowKey} after ${failureCountsRef.current[workflowKey]} consecutive failures`,
          );
          stopPolling(workflowKey);
          setWorkflowStatuses((prev) => ({
            ...prev,
            [workflowKey]: {
              status: "unknown",
              executionName: executionName,
            },
          }));
        }
        return;
      }

      const data = await response.json();

      // Reset failure count on successful response
      failureCountsRef.current[workflowKey] = 0;

      if (data.success && data.status) {
        setWorkflowStatuses((prev) => ({
          ...prev,
          [workflowKey]: {
            status: data.status,
            executionName: executionName,
          },
        }));

        // Stop polling if workflow is no longer running
        if (data.status !== "running") {
          stopPolling(workflowKey);
        }
      }
    } catch (error) {
      console.error("Error polling workflow status:", error);
      // Increment failure count on network errors
      failureCountsRef.current[workflowKey] =
        (failureCountsRef.current[workflowKey] || 0) + 1;

      // Stop polling after 5 consecutive failures
      if (failureCountsRef.current[workflowKey] >= 5) {
        console.error(
          `Stopping polling for ${workflowKey} after ${failureCountsRef.current[workflowKey]} consecutive failures`,
        );
        stopPolling(workflowKey);
        setWorkflowStatuses((prev) => ({
          ...prev,
          [workflowKey]: {
            status: "unknown",
            executionName: executionName,
          },
        }));
      }
    }
  };

  // Function to start polling
  const startPolling = (workflowKey, executionName) => {
    // Clear any existing interval for this workflow
    stopPolling(workflowKey);

    // Reset failure count when starting new polling
    failureCountsRef.current[workflowKey] = 0;

    // Set initial status to running
    setWorkflowStatuses((prev) => ({
      ...prev,
      [workflowKey]: {
        status: "running",
        executionName: executionName,
      },
    }));

    // Poll immediately
    pollWorkflowStatus(workflowKey, executionName);

    // Set up interval to poll every 15 seconds
    const intervalId = setInterval(() => {
      pollWorkflowStatus(workflowKey, executionName);
    }, POLLING_INTERVAL_MS);

    pollingIntervalsRef.current[workflowKey] = intervalId;
  };

  // Function to stop polling
  const stopPolling = (workflowKey) => {
    if (pollingIntervalsRef.current[workflowKey]) {
      clearInterval(pollingIntervalsRef.current[workflowKey]);
      pollingIntervalsRef.current[workflowKey] = null;
    }
  };

  // Function to dismiss workflow status
  const dismissWorkflowStatus = (workflowKey) => {
    stopPolling(workflowKey);
    setWorkflowStatuses((prev) => ({
      ...prev,
      [workflowKey]: {
        status: "not_started",
        executionName: null,
      },
    }));
    // Clear failure count when dismissing
    delete failureCountsRef.current[workflowKey];
  };

  const handleUploadQueryGenProcessingFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadQueryGenProcessingForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUploadQueryGenProcessingFileChange = (e) => {
    setUploadQueryGenProcessingForm((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleUploadQueryGenProcessingSubmit = async (e) => {
    e.preventDefault();

    if (
      !uploadQueryGenProcessingForm.clientId ||
      !uploadQueryGenProcessingForm.file
    ) {
      setWorkflowAlert({
        type: "danger",
        message: "Please select a client and file",
        fileId: null,
      });
      return;
    }

    setIsUploadQueryGenProcessingRunning(true);
    setWorkflowAlert(null);

    try {
      // Upload the file
      const formDataToSend = new FormData();
      formDataToSend.append("file", uploadQueryGenProcessingForm.file);

      const uploadUrl = `${BACKEND_URL}/api/v1/geo-preprocessing/upload-file?client_id=${encodeURIComponent(
        uploadQueryGenProcessingForm.clientId,
      )}&active=${uploadQueryGenProcessingForm.active}`;

      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        body: formDataToSend,
        credentials: "include",
      });

      const uploadData = await uploadResponse.json();

      if (!uploadResponse.ok || !uploadData.success) {
        // Display detailed error message from backend
        const errorMessage =
          uploadData.detail ||
          uploadData.message ||
          `File upload failed (Status: ${uploadResponse.status})`;

        setWorkflowAlert({
          type: "danger",
          message: errorMessage,
          fileId: null,
        });
        showApiError(errorMessage);
        setIsUploadQueryGenProcessingRunning(false);
        return;
      }

      // Store the file_id from upload response
      const fileId = uploadData.file_id;

      // Start query generation and processing workflow
      const workflowUrl = `${BACKEND_URL}/api/v1/geo-preprocessing/run-query-generation-onwards-workflow?file_id=${encodeURIComponent(
        fileId,
      )}`;

      const workflowResponse = await fetch(workflowUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const workflowData = await workflowResponse.json();

      if (!workflowResponse.ok || !workflowData.success) {
        const errorMessage =
          workflowData.detail || workflowData.message || "Unknown error";

        setWorkflowAlert({
          type: "warning",
          message: `File uploaded successfully (file_id: ${fileId}) but workflow failed to start: ${errorMessage}`,
          fileId: fileId,
        });
        showWarning(
          `File uploaded successfully (file_id: ${fileId}) but workflow failed to start: ${errorMessage}`,
        );
        setIsUploadQueryGenProcessingRunning(false);
        return;
      }

      if (!workflowData.execution_name) {
        const errorMessage =
          "Workflow started but no execution name was returned; polling will not be available.";
        setWorkflowAlert({
          type: "warning",
          message: `File uploaded successfully (file_id: ${fileId}) but ${errorMessage}`,
          fileId: fileId,
        });
        showWarning(
          `File uploaded successfully (file_id: ${fileId}) but ${errorMessage}`,
        );
        setIsUploadQueryGenProcessingRunning(false);
        return;
      }

      // Start polling for workflow status
      startPolling("uploadQueryGenProcessing", workflowData.execution_name);

      // Success - show success message
      setWorkflowAlert({
        type: "success",
        message:
          "File uploaded and prompt generation & processing workflow started successfully!",
        fileId: fileId,
      });
      showSuccess(
        "File uploaded and prompt generation & processing workflow started successfully!",
      );

      // Reset form
      setUploadQueryGenProcessingForm({
        clientId: "",
        file: null,
        active: false,
      });
      document.getElementById("upload-query-gen-processing-csv-input").value =
        "";
    } catch (error) {
      console.error("Error in workflow:", error);
      setWorkflowAlert({
        type: "danger",
        message: `An error occurred: ${error.message}`,
        fileId: null,
      });
      showApiError(`An error occurred: ${error.message}`);
    } finally {
      setIsUploadQueryGenProcessingRunning(false);
    }
  };

  const handleUploadFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUploadFileChange = (e) => {
    setUploadForm((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadForm.clientId || !uploadForm.file) {
      setUploadAlert({
        type: "danger",
        message: "Please select a client and file",
        fileId: null,
      });
      return;
    }

    setIsUploadLoading(true);
    setUploadAlert(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("file", uploadForm.file);

      let url = `${BACKEND_URL}/api/v1/geo-preprocessing/upload-file?client_id=${encodeURIComponent(
        uploadForm.clientId,
      )}&active=${uploadForm.active}`;
      if (uploadForm.fileId) {
        url += `&file_id=${encodeURIComponent(uploadForm.fileId)}`;
      }

      const response = await fetch(url, {
        method: "POST",
        body: formDataToSend,
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const message = data.message || "File uploaded successfully!";
        setUploadAlert({
          type: "success",
          message: message,
          fileId: data.file_id || null,
        });
        showSuccess(message);
        setUploadForm({
          clientId: "",
          fileId: "",
          file: null,
          active: false,
        });
        document.getElementById("upload-csv-input").value = "";
      } else {
        const errorMessage =
          data.detail ||
          data.message ||
          `Upload failed (Status: ${response.status})`;

        setUploadAlert({
          type: "danger",
          message: errorMessage,
          fileId: null,
        });
        showApiError(errorMessage);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadAlert({
        type: "danger",
        message: `An error occurred during upload: ${error.message}`,
        fileId: null,
      });
      showApiError(`An error occurred during upload: ${error.message}`);
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleCopyUploadFileId = () => {
    if (uploadAlert?.fileId) {
      navigator.clipboard
        .writeText(uploadAlert.fileId)
        .then(() => {
          const btn = document.getElementById("upload-copy-file-id-btn");
          if (btn) {
            const originalText = btn.textContent;
            btn.textContent = "Copied!";
            setTimeout(() => {
              btn.textContent = originalText;
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          showApiError("Failed to copy file ID");
        });
    }
  };

  const handleCopyWorkflowFileId = () => {
    if (workflowAlert?.fileId) {
      navigator.clipboard
        .writeText(workflowAlert.fileId)
        .then(() => {
          const btn = document.getElementById("workflow-copy-file-id-btn");
          const originalText = btn.textContent;
          btn.textContent = "Copied!";
          setTimeout(() => {
            btn.textContent = originalText;
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          showApiError("Failed to copy file ID");
        });
    }
  };

  // Handler functions for Curate CSV
  const handleCurateCSVFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurateCSVForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCurateCSVSubmit = async (e) => {
    e.preventDefault();

    if (!curateCSVForm.clientId) {
      setCurateCSVAlert({
        type: "danger",
        message: "Please select a client",
        fileId: null,
      });
      return;
    }

    setIsCurateCSVLoading(true);
    setCurateCSVAlert(null);

    try {
      let url = `${BACKEND_URL}/api/v1/geo-preprocessing/curate-file-using-elastic-urls?client_id=${encodeURIComponent(
        curateCSVForm.clientId,
      )}&number_of_urls=${curateCSVForm.numOfURLs ?? 500}&active=${curateCSVForm.active}`;

      if (curateCSVForm.fileId) {
        url += `&file_id=${encodeURIComponent(curateCSVForm.fileId)}`;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const message =
          data.message || "CSV curated successfully from elastic URLs!";
        setCurateCSVAlert({
          type: "success",
          message: message,
          fileId: data.file_id || null,
        });
        showSuccess(message);
        setCurateCSVForm({
          clientId: "",
          fileId: "",
          numOfURLs: null,
          active: false,
        });
      } else {
        const errorMessage =
          data.detail ||
          data.message ||
          `Curation failed (Status: ${response.status})`;

        setCurateCSVAlert({
          type: "danger",
          message: errorMessage,
          fileId: null,
        });
        showApiError(errorMessage);
      }
    } catch (error) {
      console.error("Error curating CSV:", error);
      setCurateCSVAlert({
        type: "danger",
        message: `An error occurred during curation: ${error.message}`,
        fileId: null,
      });
      showApiError(`An error occurred during curation: ${error.message}`);
    } finally {
      setIsCurateCSVLoading(false);
    }
  };

  const handleCopyCurateCSVFileId = () => {
    if (curateCSVAlert?.fileId) {
      navigator.clipboard
        .writeText(curateCSVAlert.fileId)
        .then(() => {
          const btn = document.getElementById("curate-csv-copy-file-id-btn");
          if (btn) {
            const originalText = btn.textContent;
            btn.textContent = "Copied!";
            setTimeout(() => {
              btn.textContent = originalText;
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          showApiError("Failed to copy file ID");
        });
    }
  };

  // Auto-dismiss curate CSV alerts after 60 seconds
  useEffect(() => {
    if (curateCSVAlert) {
      const timer = setTimeout(() => {
        setCurateCSVAlert(null);
      }, 60000);

      return () => clearTimeout(timer);
    }
  }, [curateCSVAlert]);

  const handleQueryGenerationSubmit = async (e) => {
    e.preventDefault();

    if (!queryGenParams.fileId) {
      showWarning("Please enter a file_id");
      return;
    }

    setIsQueryGenerationLoading(true);

    try {
      let url = `${BACKEND_URL}/api/v1/geo-preprocessing/query-generation?file_id=${encodeURIComponent(
        queryGenParams.fileId,
      )}`;

      if (queryGenParams.totalQueries !== "") {
        url += `&total_queries=${queryGenParams.totalQueries}`;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (response.ok) {
        showInfo(
          "Prompt Generation Started. You will receive a notification once it's done",
        );
        setQueryGenParams({
          fileId: "",
          totalQueries: "",
        });
      } else if (response.status === 404) {
        showApiError("File not found. Please check the file_id and try again.");
      } else {
        showApiError(
          "An error occurred while generating prompts. Please contact the dev team",
        );
      }
    } catch (error) {
      console.error("Error starting query generation:", error);
      showApiError(
        "An error occurred while generating prompts. Please contact the dev team",
      );
    } finally {
      setIsQueryGenerationLoading(false);
    }
  };

  const handleQueryProcessingSubmit = async (e) => {
    e.preventDefault();

    if (!queryProcessingFileId) {
      showWarning("Please enter a file_id");
      return;
    }

    setIsQueryProcessingLoading(true);

    try {
      const url = `${BACKEND_URL}/api/v1/geo-preprocessing/run-query-processing-onwards-workflow?file_id=${encodeURIComponent(
        queryProcessingFileId,
      )}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        // Extract execution_name and start polling if available
        if (data.success && data.execution_name) {
          startPolling("queryProcessing", data.execution_name);
        }

        showSuccess("Prompt Processing Started Successfully!");
        setQueryProcessingFileId("");
      } else if (response.status === 404) {
        showApiError("File not found. Please check the file_id and try again.");
      } else {
        showApiError(
          "An error occurred while starting prompt processing. Please contact the dev team",
        );
      }
    } catch (error) {
      console.error("Error starting query processing:", error);
      showApiError(
        "An error occurred while starting prompt processing. Please contact the dev team",
      );
    } finally {
      setIsQueryProcessingLoading(false);
    }
  };

  const handlePostprocessingSubmit = async () => {
    setIsPostprocessingLoading(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/geo-preprocessing/run-embeddings-clustering-canonicalization-workflow`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract execution_name and start polling if available
      if (data.success && data.execution_name) {
        startPolling("postprocessing", data.execution_name);
      }

      showSuccess("Postprocessing workflow started successfully!");
    } catch (error) {
      console.error("Error starting postprocessing workflow:", error);
      showApiError(
        "Failed to start postprocessing workflow. Please try again.",
      );
    } finally {
      setIsPostprocessingLoading(false);
    }
  };

  return (
    <div>
      <h4 className="h5 fw-bold mb-1">
        {t(
          "superAdmin.queryProcessing.workflowTitle",
          "Prompt Generation and Processing Workflow",
        )}
      </h4>
      <small className="text-muted mb-4 d-block">
        {t(
          "superAdmin.queryProcessing.workflowSubtitle",
          "Upload CSV and start the prompt generation and processing workflow in one step.",
        )}
      </small>
      <form onSubmit={handleUploadQueryGenProcessingSubmit}>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-2">
            <label
              htmlFor="upload-query-gen-processing-client-select"
              className="form-label"
            >
              {t("superAdmin.queryProcessing.client", "Client")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              id="upload-query-gen-processing-client-select"
              name="clientId"
              value={uploadQueryGenProcessingForm.clientId}
              onChange={handleUploadQueryGenProcessingFormChange}
              required
            >
              <option value="">
                {t(
                  "superAdmin.queryProcessing.selectClientOption",
                  "Select a client...",
                )}
              </option>
              {allClients?.data?.map((client) => (
                <option key={client.client_id} value={client.client_id}>
                  {client.client_id}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label">
              {t("superAdmin.queryProcessing.processWeekly", "Process Weekly")}{" "}
              &nbsp;
            </label>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-small ${
                  !uploadQueryGenProcessingForm.active
                    ? "btn-light"
                    : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setUploadQueryGenProcessingForm((prev) => ({
                    ...prev,
                    active: false,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.false", "False")}
              </button>
              <button
                type="button"
                className={`btn btn-small ${
                  uploadQueryGenProcessingForm.active
                    ? "btn-light"
                    : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setUploadQueryGenProcessingForm((prev) => ({
                    ...prev,
                    active: true,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.true", "True")}
              </button>
            </div>
          </div>

          <div className="col-md-2">
            <label
              htmlFor="upload-query-gen-processing-csv-input"
              className="form-label"
            >
              {t("superAdmin.queryProcessing.uploadFile", "Upload CSV")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="file"
              id="upload-query-gen-processing-csv-input"
              name="file"
              accept=".csv"
              className="form-control"
              onChange={handleUploadQueryGenProcessingFileChange}
              required
            />
          </div>

          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isUploadQueryGenProcessingRunning}
              style={{
                backgroundColor: "var(--btn-solid-bg)",
                color: "var(--btn-solid-text)",
              }}
            >
              {isUploadQueryGenProcessingRunning
                ? t(
                    "superAdmin.queryProcessing.startingWorkflow",
                    "Starting Prompt Generation and Processing...",
                  )
                : t(
                    "superAdmin.queryProcessing.startWorkflow",
                    "Start Prompt Generation and Processing",
                  )}
            </button>
            <WorkflowStatusIcon
              status={workflowStatuses.uploadQueryGenProcessing.status}
              onDismiss={() =>
                dismissWorkflowStatus("uploadQueryGenProcessing")
              }
            />
          </div>
        </div>
      </form>
      {/* Workflow Alert Display */}
      {workflowAlert && (
        <div
          className={`alert alert-${workflowAlert.type} alert-dismissible fade show mb-3`}
          role="alert"
        >
          <strong>
            {workflowAlert.type === "success"
              ? t("superAdmin.queryProcessing.success", "Success!")
              : workflowAlert.type === "warning"
                ? t(
                    "superAdmin.queryProcessing.partialSuccess",
                    "Partial Success",
                  )
                : t("superAdmin.queryProcessing.error", "Error!")}
          </strong>{" "}
          {workflowAlert.message}
          {workflowAlert.fileId && (
            <div className="mt-2">
              <div className="d-flex align-items-center gap-2">
                <span>
                  <strong>
                    {t("superAdmin.queryProcessing.fileId", "file_id")}:
                  </strong>{" "}
                  <code>{workflowAlert.fileId}</code>
                </span>
                <button
                  id="workflow-copy-file-id-btn"
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCopyWorkflowFileId}
                >
                  {t("common.copy", "Copy")}
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn-close"
            onClick={() => setWorkflowAlert(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.requiredColumnsHint",
          "Required Columns: URL/Page or Keyword/Prompt/Search Term. Optional Columns: Clicks/Count, Impressions.",
        )}
      </small>
      <br /> <hr />
      <small className="text-muted mb-2 d-block">
        {" "}
        {t(
          "superAdmin.queryProcessing.developerOnlySection",
          "Developer Only Section",
        )}{" "}
      </small>
      <h4 className="h5 fw-bold mb-1">
        {t("superAdmin.queryProcessing.uploadClientsCsv", "Upload Clients CSV")}
      </h4>
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.uploadClientsCsvHint",
          "Required Columns: URL/Page or Keyword/Prompt/Search Term. Optional Columns: Clicks/Count, Impressions. Use snake_case for file_id.",
        )}
      </small>
      <form onSubmit={handleUploadSubmit}>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-2">
            <label htmlFor="upload-client-select" className="form-label">
              {t("superAdmin.queryProcessing.client", "Client")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              id="upload-client-select"
              name="clientId"
              value={uploadForm.clientId}
              onChange={handleUploadFormChange}
              required
            >
              <option value="">
                {t(
                  "superAdmin.queryProcessing.selectClientOption",
                  "Select a client...",
                )}
              </option>
              {allClients?.data?.map((client) => (
                <option key={client.client_id} value={client.client_id}>
                  {client.client_id}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label">
              {t("superAdmin.queryProcessing.processWeekly", "Process Weekly")}{" "}
              &nbsp;
            </label>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-small ${
                  !uploadForm.active ? "btn-light" : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setUploadForm((prev) => ({
                    ...prev,
                    active: false,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.false", "False")}
              </button>
              <button
                type="button"
                className={`btn btn-small ${
                  uploadForm.active ? "btn-light" : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setUploadForm((prev) => ({
                    ...prev,
                    active: true,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.true", "True")}
              </button>
            </div>
          </div>

          <div className="col-md-2">
            <label htmlFor="upload-file-id-input" className="form-label">
              {t("superAdmin.queryProcessing.fileIdOptional", "file_id (Optional)")}
            </label>
            <input
              type="text"
              id="upload-file-id-input"
              name="fileId"
              className="form-control"
              value={uploadForm.fileId}
              onChange={handleUploadFormChange}
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="upload-csv-input" className="form-label">
              {t("superAdmin.queryProcessing.uploadFile", "Upload CSV")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="file"
              id="upload-csv-input"
              name="file"
              accept=".csv"
              className="form-control"
              onChange={handleUploadFileChange}
              required
            />
          </div>

          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isUploadLoading}
              style={{
                backgroundColor: "var(--btn-solid-bg)",
                color: "var(--btn-solid-text)",
              }}
            >
              {isUploadLoading
                ? t("superAdmin.queryProcessing.uploading", "Uploading...")
                : t("superAdmin.queryProcessing.upload", "Upload")}
            </button>
          </div>
        </div>
      </form>
      {/* Upload Alert Display */}
      {uploadAlert && (
        <div
          className={`alert alert-${uploadAlert.type} alert-dismissible fade show`}
          role="alert"
        >
          <strong>
            {uploadAlert.type === "success"
              ? t("superAdmin.queryProcessing.success", "Success!")
              : t("superAdmin.queryProcessing.error", "Error!")}
          </strong>{" "}
          {uploadAlert.message}
          {uploadAlert.fileId && (
            <div className="mt-2">
              <div className="d-flex align-items-center gap-2">
                <span>
                  <strong>
                    {t("superAdmin.queryProcessing.fileId", "file_id")}:
                  </strong>{" "}
                  <code>{uploadAlert.fileId}</code>
                </span>
                <button
                  id="upload-copy-file-id-btn"
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  onClick={handleCopyUploadFileId}
                >
                  {t("common.copy", "Copy")}
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn-close"
            onClick={() => setUploadAlert(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      <small className="text-muted mb-2 d-block">
        {" "}
        {t("superAdmin.queryProcessing.or", "or")}{" "}
      </small>
      <h4 className="h5 fw-bold mb-1">
        {t(
          "superAdmin.queryProcessing.curateCsvTitle",
          "Curate CSV file using Scraped URLs",
        )}
      </h4>
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.curateCsvSubtitle",
          "If CSV is not provided by the client, curate a list of URLs using the elastic index. This will create a CSV file that can be used for query generation and processing.",
        )}
      </small>
      <form onSubmit={handleCurateCSVSubmit}>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-2">
            <label htmlFor="curate-csv-client-select" className="form-label">
              {t("superAdmin.queryProcessing.client", "Client")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              id="curate-csv-client-select"
              name="clientId"
              value={curateCSVForm.clientId}
              onChange={handleCurateCSVFormChange}
              required
            >
              <option value="">
                {t(
                  "superAdmin.queryProcessing.selectClientOption",
                  "Select a client...",
                )}
              </option>
              {allClients?.data?.map((client) => (
                <option key={client.client_id} value={client.client_id}>
                  {client.client_id}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label">
              {t("superAdmin.queryProcessing.processWeekly", "Process Weekly")}{" "}
              &nbsp;
            </label>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-small ${
                  !curateCSVForm.active ? "btn-light" : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setCurateCSVForm((prev) => ({
                    ...prev,
                    active: false,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.false", "False")}
              </button>
              <button
                type="button"
                className={`btn btn-small ${
                  curateCSVForm.active ? "btn-light" : "btn-outline-light"
                }`}
                style={toggleButtonStyle}
                onClick={() =>
                  setCurateCSVForm((prev) => ({
                    ...prev,
                    active: true,
                  }))
                }
              >
                {t("superAdmin.queryProcessing.true", "True")}
              </button>
            </div>
          </div>

          <div className="col-md-2">
            <label htmlFor="curate-csv-file-id-input" className="form-label">
              {t("superAdmin.queryProcessing.fileIdOptional", "file_id (Optional)")}
            </label>
            <input
              type="text"
              id="curate-csv-file-id-input"
              name="fileId"
              className="form-control"
              value={curateCSVForm.fileId}
              onChange={handleCurateCSVFormChange}
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="curate-csv-num-urls-input" className="form-label">
              {t(
                "superAdmin.queryProcessing.numberOfUrls",
                "Number of URLs",
              )}
            </label>
            <input
              type="number"
              id="curate-csv-num-urls-input"
              name="numOfURLs"
              className="form-control"
              value={curateCSVForm.numOfURLs || ""}
              onChange={handleCurateCSVFormChange}
              placeholder="500"
              min="1"
            />
          </div>

          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isCurateCSVLoading}
              style={{
                backgroundColor: "var(--btn-solid-bg)",
                color: "var(--btn-solid-text)",
              }}
            >
              {isCurateCSVLoading
                ? t("superAdmin.queryProcessing.curating", "Curating...")
                : t("superAdmin.queryProcessing.curate", "Curate CSV")}
            </button>
          </div>
        </div>
      </form>
      {/* Curate CSV Alert Display */}
      {curateCSVAlert && (
        <div
          className={`alert alert-${curateCSVAlert.type} alert-dismissible fade show`}
          role="alert"
        >
          <strong>
            {curateCSVAlert.type === "success"
              ? t("superAdmin.queryProcessing.success", "Success!")
              : t("superAdmin.queryProcessing.error", "Error!")}
          </strong>{" "}
          {curateCSVAlert.message}
          {curateCSVAlert.fileId && (
            <div className="mt-2">
              <div className="d-flex align-items-center gap-2">
                <span>
                  <strong>
                    {t("superAdmin.queryProcessing.fileId", "file_id")}:
                  </strong>{" "}
                  <code>{curateCSVAlert.fileId}</code>
                </span>
                <button
                  id="curate-csv-copy-file-id-btn"
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  onClick={handleCopyCurateCSVFileId}
                >
                  {t("common.copy", "Copy")}
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn-close"
            onClick={() => setCurateCSVAlert(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      <hr />
      <h4 className="h5 fw-bold mb-1 mt-4">
        {t("superAdmin.queryProcessing.queryGeneration", "Prompt Generation")}
      </h4>
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.queryGenerationSubtitle",
          "Generate synthetic search prompts from the uploaded CSV file's URLs or keywords.",
        )}
      </small>
      <form onSubmit={handleQueryGenerationSubmit}>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-3">
            <label
              htmlFor="query-generation-file-id-input"
              className="form-label"
            >
              {t("superAdmin.queryProcessing.fileId", "file_id")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="query-generation-file-id-input"
              name="fileId"
              className="form-control"
              value={queryGenParams.fileId}
              onChange={(e) =>
                setQueryGenParams((prev) => ({
                  ...prev,
                  fileId: e.target.value,
                }))
              }
              required
            />
          </div>

          <div className="col-md-2">
            <label
              htmlFor="query-generation-total-queries"
              className="form-label"
            >
              {t(
                "superAdmin.queryProcessing.totalQueriesOptional",
                "Total Queries (Optional)",
              )}
            </label>
            <input
              type="number"
              id="query-generation-total-queries"
              className="form-control"
              value={queryGenParams.totalQueries}
              onChange={(e) =>
                setQueryGenParams((prev) => ({
                  ...prev,
                  totalQueries: e.target.value,
                }))
              }
              min="1"
              placeholder="Optional"
            />
          </div>

          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isQueryGenerationLoading}
              style={{
                backgroundColor: "var(--btn-solid-bg)",
                color: "var(--btn-solid-text)",
              }}
            >
              {isQueryGenerationLoading
                ? t("superAdmin.queryProcessing.starting", "Starting...")
                : t(
                    "superAdmin.queryProcessing.startPromptGeneration",
                    "Start Prompt Generation",
                  )}
            </button>
          </div>
        </div>
      </form>
      <h4 className="h5 fw-bold mb-1 mt-4">
        {t(
          "superAdmin.queryProcessing.queryProcessingOnwards",
          "Query Processing Onwards",
        )}
      </h4>
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.queryProcessingSubtitle",
          "Process existing prompts and fetch results across GenAI engines.",
        )}
      </small>
      <form onSubmit={handleQueryProcessingSubmit}>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-4">
            <label
              htmlFor="query-processing-file-id-input"
              className="form-label"
            >
              {t("superAdmin.queryProcessing.fileId", "file_id")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="query-processing-file-id-input"
              name="fileId"
              className="form-control"
              value={queryProcessingFileId}
              onChange={(e) => setQueryProcessingFileId(e.target.value)}
              required
            />
          </div>

          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isQueryProcessingLoading}
              style={{
                backgroundColor: "var(--btn-solid-bg)",
                color: "var(--btn-solid-text)",
              }}
            >
              {isQueryProcessingLoading
                ? t("superAdmin.queryProcessing.starting", "Starting...")
                : t(
                    "superAdmin.queryProcessing.startPromptProcessing",
                    "Start Prompt Processing",
                  )}
            </button>
            <WorkflowStatusIcon
              status={workflowStatuses.queryProcessing.status}
              onDismiss={() => dismissWorkflowStatus("queryProcessing")}
            />
          </div>
        </div>
      </form>
      <br /> <hr />
      <small className="text-muted mb-2 d-block">
        {" "}
        {t(
          "superAdmin.queryProcessing.debuggingPurposeOnly",
          "For Debugging Purpose Only",
        )}{" "}
      </small>
      <h4 className="h5 fw-bold mb-1 mt-4">
        {t(
          "superAdmin.queryProcessing.postprocessingWorkflow",
          "Embeddings, Clustering & Canonicalization Postprocessing Workflow",
        )}
      </h4>
      <small className="text-muted mb-2 d-block">
        {t(
          "superAdmin.queryProcessing.postprocessingSubtitle",
          "Run downstream embeddings, clustering, and canonicalization workflows.",
        )}
      </small>
      <button
        type="button"
        className="btn btn-primary"
        disabled={isPostprocessingLoading}
        onClick={handlePostprocessingSubmit}
        style={{
          backgroundColor: "var(--btn-solid-bg)",
          color: "var(--btn-solid-text)",
        }}
      >
        {isPostprocessingLoading
          ? t("superAdmin.queryProcessing.starting", "Starting...")
          : t(
              "superAdmin.queryProcessing.startPostprocessingWorkflow",
              "Start Postprocessing Workflow",
            )}
      </button>
      <WorkflowStatusIcon
        status={workflowStatuses.postprocessing.status}
        onDismiss={() => dismissWorkflowStatus("postprocessing")}
      />
    </div>
  );
}

export default SuperAdminQueryProcessing;
